import React, { useState, useEffect, useRef } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import axios from "axios"
import { toast } from "react-toastify"
import "./BookReadingPage.css"
import { FaPlay, FaPause, FaArrowLeft, FaArrowRight, FaLanguage } from "react-icons/fa"
import { GlobalWorkerOptions, getDocument } from "pdfjs-dist/legacy/build/pdf"
import * as pdfjsLib from "pdfjs-dist/build/pdf"
import { version } from "pdfjs-dist"

import donut_dark from "../../assets/dashboard/donut.png"
import donut_dark_bottom from "../../assets/dashboard/Donut-light-big.png"
import donut_user_yellow from "../../assets/dashboard/donut_user_yellow.png"
import circle_user_yellow from "../../assets/dashboard/circle_user_yellow.png"
import WordCheckModal from "../../Components/wordCheckModal/wordCheckModal"
import CongratulationModal from "../../Components/BookDetails/congratulationModal/congratulation"
import { updateOrInsertwordLookup, updateOrInsertBookStats } from "../../Helpers/statsApiService"
import MagicalWarningModal from "../../Components/BookDetails/MagicalWarningModal/MagicalWarningModal"
import genie from "../../assets/accounts-section/Genie.png";
import { readerLanguage } from "./readerTranslation"
import { requestStoryNarration } from "./storyNarration"
const config = require("../../Helpers/config.json")

GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.worker.js`

const BookReadingPage = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const bookData = location.state?.SelectedBook || null
    const queryParams = new URLSearchParams(location.search)
    const userLanguage = readerLanguage(queryParams.get("lang"))
    const [pagesData, setPagesData] = useState({})
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(0)
    const [loadingProgress, setLoadingProgress] = useState(0)
    const [isLoading, setIsLoading] = useState(true)
    const [audioLoadingTrigger, setAudioLoadingTrigger] = useState(0) // Used to force re-render when audio loading state changes
    const [isPlaying, setIsPlaying] = useState(false)
    const [currentWordIndex, setCurrentWordIndex] = useState(-1)
    const [words, setWords] = useState([])
    const [highlightedText, setHighlightedText] = useState("")
    const [showCongrateModal, setShowCongrateModal] = useState(false)
    const [timeSpent, setTimeSpent] = useState(0)
    const [_, forceUpdate] = useState(0);
    const [animationClass, setAnimationClass] = useState("");
    const [wordCheckModal, setWordCheckModal] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const [dictionaryResultText, setDictionaryResultText] = useState("");
    const [isTextLoading, setIsTextLoading] = useState(false);
    const [clickedWordIndex, setClickedWordIndex] = useState(-1);
    const [textSize, setTextSize] = useState(1);
    const [isDyslexicFont, setIsDyslexicFont] = useState(false);
    const [showPreferences, setShowPreferences] = useState(false);
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [isAutoNextEnabled, setIsAutoNextEnabled] = useState(false);

    // Configurable reading speed
    const WORDS_PER_MINUTE = 120;

    const audioRef = useRef(null)
    const wordTimingRef = useRef(null)
    const pdfDocumentRef = useRef(null)
    const targetPageRef = useRef(null)
    const bookContainerRef = useRef(null)
    const skipConfirmedRef = useRef(false)
    const processingRef = useRef(null)
    const playOnLoadRef = useRef(false)
    const audioGenRef = useRef({}) // Tracks currently generating pages to avoid duplicates
    const completedAudioPagesRef = useRef({})
    const backgroundPageRef = useRef(null)

    useEffect(() => {
        if (!bookData) {
            navigate("/dashboard")
        }
    }, [bookData, navigate])

    useEffect(() => {
        if (bookData?.pdfs?.[0]?.pdfPath) {
            fetchAndProcessPdf(bookData?.pdfs?.[0]?.pdfPath)
        }
    }, [])


    // Timer to track time spent on CURRENT page
    useEffect(() => {
        // Reset timer when page changes
        setTimeSpent(0);
        skipConfirmedRef.current = false;

        const interval = setInterval(() => {
            setTimeSpent(prev => prev + 1);
        }, 1000);
        return () => clearInterval(interval);
    }, [currentPage]) // Depedency on currentPage ensures reset

    useEffect(() => {
        if (currentWordIndex >= 0 && isPlaying) {
            const activeWord = document.querySelector('.highlighted-word');
            if (activeWord) {
                activeWord.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }
        }
    }, [currentWordIndex, isPlaying])


    // Sync words state with current page text
    useEffect(() => {
        const text = pagesData[currentPage]?.text;
        if (text) {
            const wordArray = text.split(' ').filter(word => word.trim() !== '');
            setWords(wordArray);
        } else {
            setWords([]);
        }
    }, [currentPage, pagesData]);

    // Auto-generate audio when page text is ready, and prefetch upcoming pages
    useEffect(() => {
        [currentPage].forEach(pageNum => {
            if (pageNum <= totalPages) {
                const page = pagesData[pageNum];
                const text = page?.text;
                if (text && !page?.audioUrl && !page?.audioLoading && !audioGenRef.current[pageNum]) {
                    console.log(`[Audio] Triggering generation for page ${pageNum}...`);
                    generateAudioSpeech(pageNum);
                }
            }
        });
    }, [pagesData, currentPage, totalPages]);

    // Prefetch page data (text/images) for upcoming pages
    useEffect(() => {
        if (!isLoading && pdfDocumentRef.current && pagesData[currentPage]?.audioUrl && backgroundPageRef.current === null) {
            [currentPage + 1].forEach(pageNum => {
                if (pageNum <= totalPages && !pagesData[pageNum]) {
                    console.log(`[Prefetch] Loading page data for page ${pageNum}...`);
                    backgroundPageRef.current = pageNum;
                    loadPageData(pageNum, pdfDocumentRef.current).finally(() => { backgroundPageRef.current = null; });
                }
            });
        }
    }, [currentPage, totalPages, pagesData, isLoading]);

    useEffect(() => {
        if (!isLoading && bookContainerRef.current) {
            setTimeout(() => {
                bookContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        }
    }, [isLoading])


    const fetchAndProcessPdf = async (urlOrPath) => {
        if (processingRef.current === urlOrPath) {
            console.log("Processing already in progress for this URL.");
            return;
        }
        processingRef.current = urlOrPath;

        try {
            let finalUrl = urlOrPath;

            // SECURITY/PERFORMANCE: If the path is a raw S3 key (doesn't start with http), fetch a signed URL
            if (!urlOrPath.startsWith("http")) {
                try {
                    const formatData = JSON.parse(localStorage.getItem("data"));
                    const authToken = formatData?.token;
                    const response = await axios.post(`${config["baseUrlAws"]}/s3/get-signed-url`,
                        { path: urlOrPath },
                        { headers: { Authorization: `Bearer ${authToken}` } }
                    );
                    if (response.data && response.data.url) {
                        finalUrl = response.data.url;
                    } else {
                        throw new Error("Failed to get signed URL from backend");
                    }
                } catch (err) {
                    console.error("Error signing URL:", err);
                    setIsLoading(false);
                    return;
                }
            }

            setLoadingProgress(0)
            const response = await axios.get(finalUrl, {
                responseType: 'arraybuffer',
                onDownloadProgress: progressEvent => {
                    const { loaded, total } = progressEvent;
                    const percentage = Math.round((loaded * 100) / total);
                    setLoadingProgress(percentage);
                }
            })

            const loadingPdfDocument = getDocument({ data: response.data })
            const pdfDocumentInstance = await loadingPdfDocument.promise
            pdfDocumentRef.current = pdfDocumentInstance
            const totalNumPages = pdfDocumentInstance.numPages
            let pages = totalNumPages;

            // Check if last page has specific text to exclude
            try {
                const lastPageData = await extractText(totalNumPages, pdfDocumentInstance, true);
                //console.log("Last Page Raw Text:", lastPageData?.rawText); // Debug log

                // Normalize text: remove extra whitespace and newlines
                const normalizedText = lastPageData?.rawText ? lastPageData.rawText.toLowerCase().replace(/\s+/g, ' ') : "";
                //console.log("Normalized Last Page Text:", normalizedText); // Debug log

                // Broad check for the footer text
                if (normalizedText.includes("explore more") || normalizedText.includes("fictiongenie")) {
                    pages = totalNumPages - 1;
                }
            } catch (e) {
                console.log("Error checking last page text:", e);
            }

            setTotalPages(pages)

            await loadPageData(currentPage, pdfDocumentInstance)
            setLoadingProgress(100)
            setIsLoading(false)
        } catch (error) {
            console.error("Error loading PDF:", error)
            setIsLoading(false)
        }
    }

    const loadPageData = async (pageNum, pdfDocumentInstance) => {
        const pageImages = await getPageImages(pageNum, pdfDocumentInstance)
        const pageText = await extractText(pageNum, pdfDocumentInstance)

        setPagesData((prevPagesData) => ({
            ...prevPagesData,
            [pageNum]: {
                ...pageImages,
                ...pageText,
                audioUrl: pageText.audioUrl || prevPagesData[pageNum]?.audioUrl || "",
                audioLoading: prevPagesData[pageNum]?.audioLoading || false,
                audioDuration: prevPagesData[pageNum]?.audioDuration || null,
            },
        }));

        return { ...pageImages, ...pageText };
    }

    const getPageImages = async (pageNum, pdfDocumentInstance) => {
        try {
            const pdfPage = await pdfDocumentInstance.getPage(pageNum);
            const operatorList = await pdfPage.getOperatorList();
            const validObjectTypes = [
                pdfjsLib.OPS.paintImageXObject,
                pdfjsLib.OPS.paintImageXObjectRepeat,
                pdfjsLib.OPS.paintJpegXObject,
            ];

            const imagePromises = operatorList.fnArray.map((element, idx) => {
                if (validObjectTypes.includes(element)) {
                    return new Promise((resolve) => {
                        const imageName = operatorList.argsArray[idx][0];
                        pdfPage.objs.get(imageName, async (image) => {
                            const imageUnit8Array = image.data;
                            const imageWidth = image.width;
                            const imageHeight = image.height;
                            const imageUint8ArrayWithAlphaChannel = addAlphaChannelToUint8ClampedArray(imageUnit8Array, imageWidth, imageHeight);
                            const imageData = new ImageData(imageUint8ArrayWithAlphaChannel, imageWidth, imageHeight);
                            const canvas = document.createElement("canvas");
                            canvas.width = imageWidth;
                            canvas.height = imageHeight;
                            const ctx = canvas.getContext("2d");
                            ctx.putImageData(imageData, 0, 0);
                            const imageSrc = canvas.toDataURL();
                            resolve({ imageSrc });
                        });
                    });
                }
            });

            const images = await Promise.all(imagePromises);
            return { images: images.filter(Boolean) };
        } catch (error) {
            console.log(error);
            return { images: [] };
        }
    };

    const addAlphaChannelToUint8ClampedArray = (unit8Array, imageWidth, imageHeight) => {
        const newImageData = new Uint8ClampedArray(imageWidth * imageHeight * 4);
        for (let j = 0, k = 0, jj = imageWidth * imageHeight * 4; j < jj;) {
            newImageData[j++] = unit8Array[k++];
            newImageData[j++] = unit8Array[k++];
            newImageData[j++] = unit8Array[k++];
            newImageData[j++] = 255;
        }
        return newImageData;
    };

    const fetchingPageRef = useRef({});

    const extractText = async (pageNum, pdfDocumentInstance, skipTranslate = false) => {
        try {
            const page = await pdfDocumentInstance.getPage(pageNum);
            const textContent = await page.getTextContent();
            const pageText = textContent.items.map((item) => item.str).join(" ");

            if (skipTranslate) {
                return { text: pageText, rawText: pageText };
            }

            const fetchKey = `${pageNum}-${userLanguage}`;
            if (!fetchingPageRef.current[fetchKey]) {
                const fileName = `Grade6_${userLanguage}_${bookData.id}_${bookData.title}_${pageNum}`;
                fetchingPageRef.current[fetchKey] = requestStoryNarration(axios, pageText, fileName, userLanguage);
            }
            return await fetchingPageRef.current[fetchKey];
        } catch (error) {
            delete fetchingPageRef.current[`${pageNum}-${userLanguage}`];
            console.error('[Narration] Page could not be prepared:', error.message);
            return { text: "", rawText: "", translationError: true };
        }
    };

    const searchDetail = (word) => {
        const age = localStorage.getItem('age')
        setIsTextLoading(true)
        setDictionaryResultText('')
        const formatData = JSON.parse(localStorage.getItem("data"));
        const authToken = formatData?.token;
        axios
            .post(config.dictionaryUrl, {
                word: word,
                ageGroup: age >= 8 ? '8-10 years' : '6-8 years'
            }, {
                headers: {
                    Authorization: `Bearer ${authToken}`
                }
            })
            .then(function ({ data }) {
                let text = data.data.meaning
                updateOrInsertwordLookup(
                    localStorage.getItem("id"),
                    localStorage.profileId,
                    bookData.id,
                    word,
                    1,
                    data.data.meaning
                );
                setDictionaryResultText(text);
                setIsTextLoading(false);
            })
            .catch(function (error) {
                console.log(error?.response?.data?.title);
                setDictionaryResultText(
                    error?.response?.data?.title
                        ? error?.response?.data?.title
                        : "Definition Not Found"
                );
                setIsTextLoading(false);
            });
    };

    const handleWordClick = (word, index) => {
        // Remove punctuation from the word
        const cleanWord = word.replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, "");
        setSearchInput(cleanWord);
        setClickedWordIndex(index);
        setWordCheckModal(true);
        searchDetail(cleanWord);
    };

    const generateAudioSpeech = async (pageNum) => {
        const page = pagesData[pageNum];
        if (!page?.text || page.translationError || audioGenRef.current[pageNum]) return;
        audioGenRef.current[pageNum] = true;
        setPagesData(previous => ({ ...previous, [pageNum]: { ...previous[pageNum], audioLoading: true } }));
        try {
            const fileName = `Grade6_${userLanguage}_${bookData.id}_${bookData.title}_${pageNum}`;
            const result = await requestStoryNarration(axios, page.rawText || page.text, fileName, userLanguage);
            setPagesData(previous => ({ ...previous, [pageNum]: { ...previous[pageNum], ...result, audioLoading: false } }));
        } catch (error) {
            setPagesData(previous => ({ ...previous, [pageNum]: { ...previous[pageNum], text: '', audioUrl: '', audioLoading: false, translationError: true } }));
        } finally {
            delete audioGenRef.current[pageNum];
            setAudioLoadingTrigger(value => value + 1);
        }
    };

    const playBook = async () => {
        const currentPageAudio = pagesData[currentPage]?.audioUrl;
        const currentPageLoading = pagesData[currentPage]?.audioLoading;

        console.log(`[Audio] playBook clicked. audioUrl: ${currentPageAudio ? 'exists' : 'null'}, audioLoading: ${currentPageLoading}`);

        if (currentPageAudio) {
            if (audioRef.current.paused) {
                console.log("[Audio] Resuming/Starting playback from existing URL.");
                audioRef.current.play();
            } else {
                console.log("[Audio] Pausing playback.");
                audioRef.current.pause();
            }
            return;
        }

        // Gesture Claiming: Prime the audio element immediately
        if (audioRef.current) {
            console.log("[Audio] Priming audio element to claim gesture context.");
            audioRef.current.load();
        }

        playOnLoadRef.current = true;

        if (currentPageLoading) {
            console.log("[Audio] Audio is currently generating. Will play automatically when ready.");
            return;
        }

        console.log("[Audio] No audio and not loading. Triggering manual generation.");
        generateAudioSpeech(currentPage);
    }

    const startWordHighlighting = () => {
        if (!audioRef.current || words.length === 0) return;

        const audio = audioRef.current;

        const startHighlighting = () => {
            if (audio.duration && !isNaN(audio.duration)) {
                const totalDuration = audio.duration;
                const wordsPerSecond = words.length / totalDuration;

                wordTimingRef.current = setInterval(() => {
                    if (audio.paused || audio.ended) {
                        clearInterval(wordTimingRef.current);
                        if (audio.ended) {
                            setIsPlaying(false);
                            setCurrentWordIndex(-1);
                        }
                        return;
                    }

                    const currentTime = audio.currentTime;
                    const expectedWordIndex = Math.floor(currentTime * wordsPerSecond);

                    if (expectedWordIndex >= 0 && expectedWordIndex < words.length) {
                        setCurrentWordIndex(expectedWordIndex);
                    }
                }, 50);
            } else {
                setTimeout(startHighlighting, 100);
            }
        };

        startHighlighting();
    };

    const pauseWordHighlighting = () => {
        if (wordTimingRef.current) {
            clearInterval(wordTimingRef.current);
            wordTimingRef.current = null;
        }
    };


    const getWordCount = (text) => {
        if (!text) return 0;
        return text.split(/\s+/).filter(word => word.length > 0).length;
    }

    const getValidAudioDuration = (duration) => {
        return Number.isFinite(duration) && duration > 0 ? duration : null;
    }

    const getCurrentAudioDuration = () => {
        if (!audioRef.current) return null;
        return getValidAudioDuration(audioRef.current.duration);
    }

    const saveCurrentAudioDuration = () => {
        const duration = getCurrentAudioDuration();
        if (!duration) return;

        const roundedDuration = Math.ceil(duration);
        setPagesData(prev => {
            const pageData = prev[currentPage];
            if (!pageData || pageData.audioDuration === roundedDuration) return prev;

            return {
                ...prev,
                [currentPage]: {
                    ...pageData,
                    audioDuration: roundedDuration,
                }
            };
        });
    }

    const calculateReadingRequirement = (text, pageNum = currentPage) => {
        const wordCount = getWordCount(text);
        if (wordCount === 0) {
            return {
                requiredSeconds: 0,
                wordCount,
                source: "empty",
                wordsPerMinute: WORDS_PER_MINUTE,
                audioDuration: null,
            };
        }

        let audioDuration = getValidAudioDuration(pagesData[pageNum]?.audioDuration);

        if (pageNum === currentPage) {
            audioDuration = getCurrentAudioDuration() || audioDuration;
        }

        if (audioDuration) {
            const dynamicWordsPerMinute = Math.max(1, Math.round((wordCount / audioDuration) * 60));

            return {
                requiredSeconds: Math.ceil(audioDuration),
                wordCount,
                source: "audio",
                wordsPerMinute: dynamicWordsPerMinute,
                audioDuration: Math.ceil(audioDuration),
            };
        }

        return {
            requiredSeconds: Math.ceil((wordCount / WORDS_PER_MINUTE) * 60),
            wordCount,
            source: "word-estimate",
            wordsPerMinute: WORDS_PER_MINUTE,
            audioDuration: null,
        };
    }

    const nextPage = async (isAuto = false) => {
        const isAutoAdvance = isAuto === true;

        if (currentPage < totalPages) {

            // Check reading progress before leaving current page
            const currentText = pagesData[currentPage]?.text || "";
            const readingRequirement = calculateReadingRequirement(currentText, currentPage);
            const { requiredSeconds, wordCount, source, wordsPerMinute, audioDuration } = readingRequirement;
            const isAudioCompleted = !!completedAudioPagesRef.current[currentPage];
            const audioDurationText = audioDuration ? `${audioDuration}s` : "n/a";

            // Log for debugging
            console.log(`Page: ${currentPage}, Words: ${wordCount}, Required: ${requiredSeconds}s, Source: ${source}, Audio Duration: ${audioDurationText}, Dynamic WPM: ${wordsPerMinute}, Spent: ${timeSpent}s, Audio Completed: ${isAudioCompleted}`);

            if (isAutoAdvance || isAudioCompleted || timeSpent >= requiredSeconds) {
                if (requiredSeconds > 0) {
                    updateOrInsertBookStats(localStorage.getItem("id"), localStorage.profileId, bookData.id, currentPage, totalPages, false)
                }
            }

            const nextIdx = currentPage + 1;

            // Ensure data for next page is ready BEFORE animation
            if (!pagesData[nextIdx]?.text) {
                targetPageRef.current = nextIdx;
                setIsLoading(true);
                if (pdfDocumentRef.current) {
                    await loadPageData(nextIdx, pdfDocumentRef.current);
                }
                setIsLoading(false);
                targetPageRef.current = null;
            }

            // 2. Then trigger animation
            setAnimationClass("animate-flip-next");

            setTimeout(async () => {
                console.log(`[Audio] Navigating to page ${nextIdx}. Resetting audio states.`);
                // We no longer clear audioUrl here because it's stored in pagesData
                setIsPlaying(false);
                setCurrentWordIndex(-1);

                // If this was an auto-next, we want to play the next page automatically
                if (isAutoAdvance) {
                    playOnLoadRef.current = true;
                } else {
                    playOnLoadRef.current = false;
                }

                if (timeSpent >= 10) {
                    // updateOrInsertBookStats(localStorage.getItem("id"), localStorage.profileId, bookData.id, currentPage + 2)
                }

                setCurrentPage(nextIdx);
            }, 300); // Wait for half animation

            setTimeout(() => {
                setAnimationClass("");
            }, 600); // Clear animation class
        } else {
            // Finish book
            // Check last page reading
            const currentText = pagesData[currentPage]?.text || "";
            const readingRequirement = calculateReadingRequirement(currentText, currentPage);
            const { requiredSeconds } = readingRequirement;
            const isAudioCompleted = !!completedAudioPagesRef.current[currentPage];

            if (isAutoAdvance || isAudioCompleted || timeSpent >= requiredSeconds) {
                if (requiredSeconds > 0) {
                    updateOrInsertBookStats(localStorage.getItem("id"), localStorage.profileId, bookData.id, currentPage, totalPages, true)
                }
            }

            if (true) { // Always allow finish, but stats updated above
                // updateOrInsertBookStats(localStorage.getItem("id"), localStorage.profileId, bookData.id, currentPage + 2)
                setShowCongrateModal(true);
            } else {
                navigate("/dashboard");
            }
        }
    }

    const prevPage = () => {
        if (currentPage > 1) {
            setAnimationClass("animate-flip-prev");

            setTimeout(async () => {
                const prevIdx = currentPage - 1;

                // Ensure data for prev page is ready (though it usually is)
                if (!pagesData[prevIdx]?.text) {
                    setIsLoading(true);
                    if (pdfDocumentRef.current) {
                        await loadPageData(prevIdx, pdfDocumentRef.current);
                    }
                    setIsLoading(false);
                }

                console.log(`[Audio] Navigating back to page ${prevIdx}. Resetting audio states.`);
                // We no longer clear audioUrl here
                setIsPlaying(false);
                setCurrentWordIndex(-1);
                playOnLoadRef.current = false;
                setCurrentPage(prevIdx);
            }, 300);

            setTimeout(() => {
                setAnimationClass("");
            }, 600);
        }
    }

    return (
        <div className="book-reading-page">
            <img src={donut_user_yellow} className="decoration-donut-1" alt="" />

            {/* <img src={donut_user_yellow} className="dashbaord-donut-bottom" alt="" /> */}
            <img src={donut_user_yellow} className="decoration-donut-2" alt="" />

            {isLoading && (
                <div className="loading-overlay">
                    <div className="loading-spinner"></div>
                    <div>Loading Story... {loadingProgress}%</div>
                </div>
            )}

            <div ref={bookContainerRef} className={`book-reading-content ${animationClass}`}>
                <div className="book-reading-header">
                    <button className="header-back-btn" onClick={() => navigate("/dashboard")}>
                        <FaArrowLeft />
                    </button>
                    <div className="header-title-container">
                        <h2 className="header-title">{bookData?.title}</h2>
                        {/* <span className="header-subtitle">INTERACTIVE BOOK</span> */}
                    </div>

                    <div className="header-controls">
                        {/* <button className="lang-btn">
                            <FaLanguage className="lang-icon" />
                            <span>English</span>
                        </button> */}
                        <button
                            className={`auto-play-btn ${isAutoNextEnabled ? 'active' : ''}`}
                            onClick={() => setIsAutoNextEnabled(!isAutoNextEnabled)}
                            title={isAutoNextEnabled ? 'Auto-play on: pages turn after narration finishes. Click to turn off.' : 'Turn on auto-play to advance pages after narration finishes.'}
                            aria-label="Automatically turn pages after narration"
                            aria-pressed={isAutoNextEnabled}
                        >
                            <FaPlay aria-hidden="true" className="auto-play-icon" />
                            {isAutoNextEnabled && <span className="auto-play-label">Auto Next Page</span>}
                        </button>

                        <button
                            className="text-settings-btn"
                            onClick={() => setShowPreferences(!showPreferences)}
                        >
                            <span style={{ fontFamily: "'Times New Roman', serif", fontWeight: "bold", fontSize: "1.3rem" }}>T</span>
                        </button>

                        {showPreferences && (
                            <div className="preferences-popover">
                                <div className="popover-header">
                                    <h3>Reading Preferences</h3>
                                </div>
                                <div className="pref-section">
                                    <label>TEXT SIZE</label>
                                    <div className="text-size-controls">
                                        <button
                                            className={`size-btn small ${textSize === 1 ? 'active' : ''}`}
                                            onClick={() => setTextSize(1)}
                                        >
                                            Aa
                                        </button>
                                        <button
                                            className={`size-btn large ${textSize === 1.25 ? 'active' : ''}`}
                                            onClick={() => setTextSize(1.25)}
                                        >
                                            Aa
                                        </button>
                                    </div>
                                </div>
                                <div className="pref-section">
                                    <label>DYSLEXIA FRIENDLY</label>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={isDyslexicFont}
                                            onChange={() => setIsDyslexicFont(!isDyslexicFont)}
                                        />
                                        <span className="slider round"></span>
                                    </label>
                                </div>
                                <p className="dyslexia-info">
                                    Changes font to OpenDyslexic to improve readability.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="book-reading-body">
                    <div className="book-image-section">
                        <div className="book-image-container">
                            {pagesData[currentPage]?.images?.[0]?.imageSrc && (
                                <img
                                    src={pagesData[currentPage].images[0].imageSrc}
                                    alt="Story illustration"
                                    className="book-image"
                                />
                            )}
                        </div>
                    </div>

                    <div className="book-content-section">
                        {pagesData[currentPage]?.translationError && <div className="reader-translation-error" role="alert">
                            <p>The {userLanguage} story and narration could not be loaded. Please try again.</p>
                            <button onClick={async () => {
                                setPagesData(previous => ({ ...previous, [currentPage]: { ...previous[currentPage], translationRetrying: true } }));
                                await loadPageData(currentPage, pdfDocumentRef.current);
                            }} disabled={pagesData[currentPage]?.translationRetrying}>{pagesData[currentPage]?.translationRetrying ? 'Retrying…' : 'Retry story'}</button>
                        </div>}
                        <div
                            className={`book-text-container ${isDyslexicFont ? 'font-dyslexic' : ''} ${['urdu', 'arabic', 'ur', 'ar'].includes(userLanguage.toLowerCase()) ? 'text-rtl' : ''}`}
                            translate="no"
                            style={{ fontSize: `${1.2 * textSize}rem` }}
                        >
                            {words.map((word, index) => (
                                <span
                                    key={index}
                                    className={`${index === currentWordIndex ? "highlighted-word" : ""} ${index === clickedWordIndex ? "selected-word" : ""} word-span`}
                                    style={{ marginRight: '5px', display: 'inline-block', cursor: 'pointer' }}
                                    onClick={() => handleWordClick(word, index)}
                                >
                                    {word}
                                </span>
                            ))}
                        </div>


                    </div>
                </div>

                <div className="book-reading-footer">
                    <div
                        className="footer-progress-bar"
                        style={{ width: `${(currentPage / totalPages) * 100}%` }}
                    />
                    <div className="footer-audio-controls">
                        <button className="play-pause-btn footer-btn" onClick={playBook} disabled={pagesData[currentPage]?.audioLoading || !pagesData[currentPage]?.text || pagesData[currentPage]?.translationError}>
                            {pagesData[currentPage]?.audioLoading ? "" : isPlaying ? <FaPause /> : <FaPlay />}
                        </button>
                        <span className="footer-text">Read to me</span>
                    </div>

                    <div className="footer-navigation-controls">
                        <button className="nav-btn footer-nav-btn" onClick={prevPage} disabled={currentPage === 1}>
                            <FaArrowLeft />
                        </button>
                        <span className="page-info-text">Page {currentPage} / {totalPages}</span>
                        {currentPage === totalPages ? (
                            <button className="nav-btn footer-nav-btn finish-btn" onClick={() => nextPage()}>
                                Finish
                            </button>
                        ) : (
                            <button className="nav-btn footer-nav-btn" onClick={() => nextPage()}>
                                <FaArrowRight />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <audio
                ref={audioRef}
                src={pagesData[currentPage]?.audioUrl}
                onLoadedMetadata={saveCurrentAudioDuration}
                onDurationChange={saveCurrentAudioDuration}
                onCanPlay={() => {
                    saveCurrentAudioDuration();
                    console.log(`[Audio] onCanPlay event fired. playOnLoadRef: ${playOnLoadRef.current}`);
                    if (playOnLoadRef.current) {
                        console.log("[Audio] playback triggered via onCanPlay.");
                        audioRef.current.play().catch(err => {
                            console.error("[Audio] Playback failed:", err);
                        });
                        playOnLoadRef.current = false;
                    }
                }}
                onPlay={() => {
                    setIsPlaying(true);
                    startWordHighlighting();
                }}
                onPause={() => {
                    setIsPlaying(false);
                    pauseWordHighlighting();
                }}
                onEnded={() => {
                    completedAudioPagesRef.current[currentPage] = true;
                    setIsPlaying(false);
                    setCurrentWordIndex(-1);
                    if (isAutoNextEnabled) {
                        console.log("[Audio] Auto-next triggered.");
                        nextPage(true);
                    }
                }}
                onTimeUpdate={() => {
                    // Force re-render for progress bar
                    forceUpdate(n => n + 1);
                }}
            />

            {/* Genie Audio Loader Overlay */}
            {pagesData[currentPage]?.audioLoading && (
                <div className="loading-overlay">
                    <div className="audio-genie-container" style={{ animation: 'float 2s ease-in-out infinite' }}>
                        <img
                            src={genie}
                            alt="Loading Genie"
                            className="audio-genie-img"
                            style={{ filter: 'drop-shadow(0 0 10px rgba(106, 13, 173, 0.3))' }} // Adjusted shadow to match purple theme
                        />
                    </div>
                    {/* Reusing style from standard loader text (inherited from .loading-overlay) */}
                    {/* <div>Loading Page....</div> */}
                </div>
            )}

            {showCongrateModal && (
                <CongratulationModal
                    showCongrateModal={showCongrateModal}
                    setShowCongrateModal={setShowCongrateModal}
                />
            )}

            <WordCheckModal
                userLanguage={userLanguage}
                dictionaryResultText={dictionaryResultText}
                wordCheckModal={wordCheckModal}
                isLoading={isTextLoading}
                searchInput={searchInput}
                setWordCheckModal={(val) => {
                    setWordCheckModal(val);
                    if (!val) setClickedWordIndex(-1);
                }}
                bookData={bookData}
                selectedText={searchInput}
                isTextLoading={isTextLoading}
            />

            <MagicalWarningModal
                show={showWarningModal}
                onHide={() => setShowWarningModal(false)}
            />
        </div>
    )
}

export default BookReadingPage
